import { t } from "i18next";

import {
  SimpleResult,
  simpleResultErrorEmptyResponse,
  simpleResultErrorTechnicalError,
  warnAndToastSimpleResult,
} from "../dto";
import {
  Api,
  getBulkPassengersFutureBookingsImpactPath,
  getBulkShiftsFutureBookingsImpactPath,
  getCompanyId,
  getPassengerFutureBookingsImpactPath,
  getShiftFutureBookingsImpactPath,
  Log,
} from "../helpers";
import {
  IFutureBookingsImpactCount,
  IPassengerStationsChange,
  IShiftStationOptionsChange,
} from "../models";

/**
 * Surfaces why an impact preview failed instead of silently collapsing it to "no count".
 *
 * The count decides whether a mass station change is about to happen, so a failure the user cannot
 * see is the worst outcome: it looks identical to "nothing would change". The toast names the real
 * reason, and `undefined` still flows back to the caller, which reads it as PreviewFailed and aborts
 * the save rather than cascading an unapproved change.
 *
 * `t` comes from the static i18next instance because this runs outside React.
 */
const readImpactCount = (
  response: { data?: SimpleResult<IFutureBookingsImpactCount> } | undefined,
): number | undefined => {
  const result = response?.data;

  if (!result) {
    warnAndToastSimpleResult(
      simpleResultErrorEmptyResponse<IFutureBookingsImpactCount>(t),
      t,
    );

    return undefined;
  }

  if (!result.success) {
    warnAndToastSimpleResult(result, t);

    return undefined;
  }

  return result.data?.count;
};

/**
 * Runs an impact request so that EVERY failure mode is reported the same way, in one place:
 * a failed SimpleResult, an empty response, and a thrown request (network / 5xx / permission) all
 * toast once here and return `undefined`. Callers read that as PreviewFailed and abort the save.
 */
const requestImpactCount = async (
  request: () => Promise<
    { data?: SimpleResult<IFutureBookingsImpactCount> } | undefined
  >,
): Promise<number | undefined> => {
  try {
    return readImpactCount(await request());
  } catch (error) {
    Log.debug("Future bookings impact request threw", error);
    warnAndToastSimpleResult(
      simpleResultErrorTechnicalError<IFutureBookingsImpactCount>(t),
      t,
    );

    return undefined;
  }
};

/**
 * Asks the server how many future "New"/"Draft" bookings a pending station change would move
 * (RYDE-7929). Read-only — nothing is saved until the user approves the popup this count gates.
 */
export class FutureBookingsImpactApi {
  static async forShift(
    shiftId: number,
    payload: IShiftStationOptionsChange,
  ): Promise<number | undefined> {
    const companyId = getCompanyId()!;
    const requestUrl = getShiftFutureBookingsImpactPath(companyId, shiftId);
    return requestImpactCount(() =>
      Api.post<SimpleResult<IFutureBookingsImpactCount>>(requestUrl, payload),
    );
  }

  static async forShiftsBulk(
    ids: number[],
    payload: IShiftStationOptionsChange,
  ): Promise<number | undefined> {
    const companyId = getCompanyId()!;
    const requestUrl = getBulkShiftsFutureBookingsImpactPath(companyId);
    return requestImpactCount(() =>
      Api.post<SimpleResult<IFutureBookingsImpactCount>>(requestUrl, {
        ids,
        payload,
      }),
    );
  }

  static async forPassenger(
    passengerId: number,
    payload: IPassengerStationsChange,
  ): Promise<number | undefined> {
    const companyId = getCompanyId()!;
    const requestUrl = getPassengerFutureBookingsImpactPath(
      companyId,
      passengerId,
    );
    return requestImpactCount(() =>
      Api.post<SimpleResult<IFutureBookingsImpactCount>>(requestUrl, payload),
    );
  }

  static async forPassengersBulk(
    ids: number[],
    payload: IPassengerStationsChange,
  ): Promise<number | undefined> {
    const companyId = getCompanyId()!;
    const requestUrl = getBulkPassengersFutureBookingsImpactPath(companyId);
    return requestImpactCount(() =>
      Api.post<SimpleResult<IFutureBookingsImpactCount>>(requestUrl, {
        ids,
        payload,
      }),
    );
  }
}
